import React, { useState, useEffect, useMemo } from 'react';
import { Server, Video, ChevronRight, ChevronLeft, Search, FolderOpen, Folder } from 'lucide-react';
import { Modal, SEARCH_INPUT } from '@trinetra/shared';

interface Camera {
  cameraId: string;
  cameraName: string;
  groupIds?: string[];
}

interface Group {
  id: string;
  name: string;
  parent_id: string | null;
}

interface NodeWithCameras {
  nodeId: string;
  nodeName: string;
  status: string;
  cameras: Camera[];
  groups?: Group[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (nodeId: string, nodeName: string, cameraId: string, cameraName: string) => void;
  nodes: NodeWithCameras[];
  loading: boolean;
  usedCameraKeys: Set<string>;
}

type DrillLevel = 'nodes' | 'groups' | 'cameras';

export default function CameraPickerModal({ open, onClose, onSelect, nodes, loading, usedCameraKeys }: Props) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [drillLevel, setDrillLevel] = useState<DrillLevel>('nodes');
  const [search, setSearch] = useState('');

  // Reset when opened
  useEffect(() => {
    if (open) {
      setSelectedNodeId(null);
      setSelectedGroupId(null);
      setDrillLevel('nodes');
      setSearch('');
    }
  }, [open]);

  const selectedNode = nodes.find((n) => n.nodeId === selectedNodeId) ?? null;
  const onlineNodes = nodes.filter((n) => n.status === 'online');

  // Build group tree and ungrouped cameras for selected node
  const { topLevelGroups, ungroupedCameras, groupCamerasMap } = useMemo(() => {
    if (!selectedNode) return { topLevelGroups: [] as Group[], ungroupedCameras: [] as Camera[], groupCamerasMap: new Map<string, Camera[]>() };

    const groups = selectedNode.groups || [];
    const cameras = selectedNode.cameras || [];

    // Top-level groups (no parent)
    const topLevel = groups.filter((g) => !g.parent_id);

    // Build map: groupId → child groups
    const childGroupMap = new Map<string, Group[]>();
    for (const g of groups) {
      if (g.parent_id) {
        const arr = childGroupMap.get(g.parent_id) || [];
        arr.push(g);
        childGroupMap.set(g.parent_id, arr);
      }
    }

    // Build map: groupId → cameras in that group
    const camMap = new Map<string, Camera[]>();
    const groupedCameraIds = new Set<string>();

    for (const cam of cameras) {
      if (cam.groupIds && cam.groupIds.length > 0) {
        for (const gid of cam.groupIds) {
          const arr = camMap.get(gid) || [];
          arr.push(cam);
          camMap.set(gid, arr);
          groupedCameraIds.add(cam.cameraId);
        }
      }
    }

    // Cameras not in any group
    const ungrouped = cameras.filter((c) => !groupedCameraIds.has(c.cameraId));

    return { topLevelGroups: topLevel, ungroupedCameras: ungrouped, groupCamerasMap: camMap };
  }, [selectedNode]);

  // Get cameras for selected group (including child groups' cameras shown inline)
  const selectedGroupCameras = useMemo(() => {
    if (!selectedGroupId) return [];
    return groupCamerasMap.get(selectedGroupId) || [];
  }, [selectedGroupId, groupCamerasMap]);

  // Get child groups for selected group
  const childGroups = useMemo(() => {
    if (!selectedNode || !selectedGroupId) return [];
    return (selectedNode.groups || []).filter((g) => g.parent_id === selectedGroupId);
  }, [selectedNode, selectedGroupId]);

  // Filter by search
  const filterBySearch = (name: string) => !search || name.toLowerCase().includes(search.toLowerCase());

  if (!open) return null;

  // Navigation
  function goToNode(nodeId: string) {
    setSelectedNodeId(nodeId);
    setSelectedGroupId(null);
    setDrillLevel('groups');
    setSearch('');
  }

  function goToGroup(groupId: string) {
    setSelectedGroupId(groupId);
    setDrillLevel('cameras');
    setSearch('');
  }

  function goBack() {
    if (drillLevel === 'cameras') {
      // Check if this group has a parent group
      const currentGroup = (selectedNode?.groups || []).find((g) => g.id === selectedGroupId);
      if (currentGroup?.parent_id) {
        setSelectedGroupId(currentGroup.parent_id);
      } else {
        setSelectedGroupId(null);
        setDrillLevel('groups');
      }
    } else if (drillLevel === 'groups') {
      setSelectedNodeId(null);
      setDrillLevel('nodes');
    }
    setSearch('');
  }

  // Title
  const title = drillLevel === 'nodes'
    ? 'Select Site'
    : drillLevel === 'groups'
    ? selectedNode?.nodeName || 'Select Group'
    : (() => {
        const grp = (selectedNode?.groups || []).find((g) => g.id === selectedGroupId);
        return grp ? grp.name : selectedNode?.nodeName || 'Cameras';
      })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      fillBody
      ariaLabel={title}
      title={
        <span className="flex min-w-0 items-center gap-2">
          {drillLevel !== 'nodes' && (
            <button
              type="button"
              onClick={goBack}
              className="-ml-1 flex-shrink-0 rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="Back"
              aria-label="Back"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <span className="truncate">{title}</span>
        </span>
      }
    >
          {/* Search (groups and cameras levels) */}
          {drillLevel !== 'nodes' && (
            <div className="flex-shrink-0 px-4 py-2 border-b border-border">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={drillLevel === 'groups' ? 'Search groups or cameras…' : 'Search cameras…'}
                  className={SEARCH_INPUT}
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-brand" />
                <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
              </div>
            ) : drillLevel === 'nodes' ? (
              /* ─── Node list ─── */
              <div className="py-1">
                {onlineNodes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No online sites</p>
                ) : (
                  onlineNodes.map((node) => (
                    <button
                      key={node.nodeId}
                      onClick={() => goToNode(node.nodeId)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                    >
                      <Server className="h-4 w-4 text-brand flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground font-medium truncate">
                        {node.nodeName}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {node.cameras.length} cameras
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            ) : drillLevel === 'groups' ? (
              /* ─── Groups + ungrouped cameras ─── */
              <div className="py-1">
                {/* Groups */}
                {topLevelGroups.filter((g) => filterBySearch(g.name)).map((group) => {
                  const camCount = (groupCamerasMap.get(group.id) || []).length;
                  const childCount = (selectedNode?.groups || []).filter((g) => g.parent_id === group.id).length;
                  return (
                    <button
                      key={group.id}
                      onClick={() => goToGroup(group.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                    >
                      <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground font-medium truncate">
                        {group.name}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {camCount} cam{camCount !== 1 ? 's' : ''}
                        {childCount > 0 && ` · ${childCount} sub`}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}

                {/* Separator if both groups and ungrouped exist */}
                {topLevelGroups.length > 0 && ungroupedCameras.length > 0 && (
                  <div className="px-4 py-1.5">
                    <div className="border-t border-border/50" />
                    <p className="text-[10px] text-muted-foreground mt-1.5 font-medium uppercase tracking-wider">Ungrouped</p>
                  </div>
                )}

                {/* Ungrouped cameras shown directly */}
                {ungroupedCameras.filter((c) => filterBySearch(c.cameraName)).map((cam) => {
                  const key = `${selectedNode!.nodeId}:${cam.cameraId}`;
                  const isUsed = usedCameraKeys.has(key);
                  return (
                    <button
                      key={cam.cameraId}
                      onClick={() => {
                        if (!isUsed) {
                          onSelect(selectedNode!.nodeId, selectedNode!.nodeName, cam.cameraId, cam.cameraName);
                          onClose();
                        }
                      }}
                      disabled={isUsed}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        isUsed ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'
                      }`}
                    >
                      <Video className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground truncate">{cam.cameraName}</span>
                      {isUsed && <span className="text-[10px] text-muted-foreground">In grid</span>}
                    </button>
                  );
                })}

                {/* Empty state */}
                {topLevelGroups.length === 0 && ungroupedCameras.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No cameras at this site</p>
                )}
              </div>
            ) : (
              /* ─── Cameras in selected group ─── */
              <div className="py-1">
                {/* Child groups (sub-groups) */}
                {childGroups.filter((g) => filterBySearch(g.name)).map((group) => {
                  const camCount = (groupCamerasMap.get(group.id) || []).length;
                  return (
                    <button
                      key={group.id}
                      onClick={() => goToGroup(group.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors"
                    >
                      <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground font-medium truncate">
                        {group.name}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {camCount} cam{camCount !== 1 ? 's' : ''}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}

                {/* Cameras in this group */}
                {childGroups.length > 0 && selectedGroupCameras.length > 0 && (
                  <div className="px-4 py-1.5">
                    <div className="border-t border-border/50" />
                    <p className="text-[10px] text-muted-foreground mt-1.5 font-medium uppercase tracking-wider">Cameras</p>
                  </div>
                )}

                {selectedGroupCameras.filter((c) => filterBySearch(c.cameraName)).map((cam) => {
                  const key = `${selectedNode!.nodeId}:${cam.cameraId}`;
                  const isUsed = usedCameraKeys.has(key);
                  return (
                    <button
                      key={cam.cameraId}
                      onClick={() => {
                        if (!isUsed) {
                          onSelect(selectedNode!.nodeId, selectedNode!.nodeName, cam.cameraId, cam.cameraName);
                          onClose();
                        }
                      }}
                      disabled={isUsed}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        isUsed ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted cursor-pointer'
                      }`}
                    >
                      <Video className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground truncate">{cam.cameraName}</span>
                      {isUsed && <span className="text-[10px] text-muted-foreground">In grid</span>}
                    </button>
                  );
                })}

                {/* Empty state */}
                {childGroups.length === 0 && selectedGroupCameras.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {search ? 'No cameras match' : 'No cameras in this group'}
                  </p>
                )}
              </div>
            )}
          </div>
    </Modal>
  );
}
